import { useState } from 'react';
import {
  Trash2,
  Edit2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronUp,
  ChevronDown,
  Maximize2,
  Minimize2,
  BarChart2,
  Table2,
  ImageIcon,
} from 'lucide-react';
import { useStore } from '../store';
import type {
  VisualBlock as VisualBlockType,
  ImageVisualBlock,
  ChartVisualBlock,
  TableVisualBlock,
} from '../types/visualizations';
import { isImageBlock, isChartBlock, isTableBlock } from '../types/visualizations';
import ChartBlock from './visualizations/ChartBlock';
import TableBlock from './visualizations/TableBlock';

interface VisualBlockProps {
  block: VisualBlockType;
  sectionId: number;
  onEdit?: () => void;
}

const SIZE_CLASSES = {
  small: 'max-w-[250px]',
  medium: 'max-w-[450px]',
  large: 'max-w-[650px]',
  full: 'max-w-full',
};

const ALIGNMENT_CLASSES = {
  left: 'mr-auto',
  center: 'mx-auto',
  right: 'ml-auto',
};

export function VisualBlockComponent({ block, sectionId, onEdit }: VisualBlockProps) {
  const { updateVisualBlock, removeVisualBlock } = useStore();
  const [isEditing, setIsEditing] = useState(false);
  const [caption, setCaption] = useState(block.caption || '');
  const [showControls, setShowControls] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleSizeChange = (width: VisualBlockType['width']) => {
    updateVisualBlock(sectionId, block.id, { width });
  };

  const handleAlignmentChange = (alignment: VisualBlockType['alignment']) => {
    updateVisualBlock(sectionId, block.id, { alignment });
  };

  const handlePositionChange = (position: VisualBlockType['position']) => {
    updateVisualBlock(sectionId, block.id, { position });
  };

  const handleCaptionSave = () => {
    updateVisualBlock(sectionId, block.id, { caption });
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to remove this visual element?')) {
      removeVisualBlock(sectionId, block.id);
    }
  };

  const renderContent = () => {
    if (isImageBlock(block)) {
      return renderImageContent(block);
    }
    if (isChartBlock(block)) {
      return renderChartContent(block);
    }
    if (isTableBlock(block)) {
      return renderTableContent(block);
    }
    return null;
  };

  const renderImageContent = (imgBlock: ImageVisualBlock) => (
    <div className={`relative ${!imageLoaded ? 'min-h-[200px] bg-gray-100 animate-pulse rounded-lg' : ''}`}>
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-gray-400 animate-pulse" />
        </div>
      )}
      <img
        src={imgBlock.imageUrl}
        alt={imgBlock.caption || 'Section image'}
        className={`w-full rounded-lg shadow-md ${!imageLoaded ? 'hidden' : ''}`}
        onLoad={() => setImageLoaded(true)}
      />
    </div>
  );

  const renderChartContent = (chartBlock: ChartVisualBlock) => (
    <ChartBlock config={chartBlock.config} />
  );

  const renderTableContent = (tableBlock: TableVisualBlock) => (
    <TableBlock config={tableBlock.config} />
  );

  const getBlockIcon = () => {
    if (isImageBlock(block)) return ImageIcon;
    if (isChartBlock(block)) return BarChart2;
    if (isTableBlock(block)) return Table2;
    return ImageIcon;
  };

  const BlockIcon = getBlockIcon();

  return (
    <div
      className={`relative group my-4 ${ALIGNMENT_CLASSES[block.alignment]}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* Content Container */}
      <div className={`relative ${SIZE_CLASSES[block.width]}`}>
        {renderContent()}

        {/* Hover Controls */}
        {showControls && (
          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center transition-opacity z-10">
            <div className="bg-white rounded-xl shadow-xl p-3 max-w-[280px]">
              {/* Block Type Indicator */}
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                <BlockIcon className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase">
                  {block.type === 'image' ? 'Image' : block.type === 'chart' ? 'Chart' : 'Table'}
                </span>
              </div>

              {/* Size */}
              <div className="mb-3">
                <label className="text-[10px] font-medium text-gray-500 uppercase mb-1.5 block">
                  Size
                </label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  {([
                    { id: 'small', label: 'S', icon: Minimize2 },
                    { id: 'medium', label: 'M', icon: null },
                    { id: 'large', label: 'L', icon: null },
                    { id: 'full', label: 'Full', icon: Maximize2 },
                  ] as const).map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handleSizeChange(id)}
                      className={`flex-1 py-1.5 px-2 text-xs font-medium flex items-center justify-center gap-1 ${
                        block.width === id
                          ? 'bg-accent text-white'
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      {Icon && <Icon className="w-3 h-3" />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Alignment */}
              <div className="mb-3">
                <label className="text-[10px] font-medium text-gray-500 uppercase mb-1.5 block">
                  Alignment
                </label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  {([
                    { id: 'left', label: 'Left', icon: AlignLeft },
                    { id: 'center', label: 'Center', icon: AlignCenter },
                    { id: 'right', label: 'Right', icon: AlignRight },
                  ] as const).map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handleAlignmentChange(id)}
                      className={`flex-1 py-1.5 px-2 text-xs font-medium flex items-center justify-center gap-1 ${
                        block.alignment === id
                          ? 'bg-accent text-white'
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Position */}
              <div className="mb-3">
                <label className="text-[10px] font-medium text-gray-500 uppercase mb-1.5 block">
                  Position
                </label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  {([
                    { id: 'before', label: 'Before', icon: ChevronUp },
                    { id: 'middle', label: 'Middle', icon: null },
                    { id: 'after', label: 'After', icon: ChevronDown },
                  ] as const).map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handlePositionChange(id)}
                      className={`flex-1 py-1.5 px-2 text-xs font-medium flex items-center justify-center gap-1 ${
                        block.position === id
                          ? 'bg-accent text-white'
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      {Icon && <Icon className="w-3 h-3" />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                {onEdit && isImageBlock(block) && (
                  <button
                    onClick={onEdit}
                    className="flex-1 py-1.5 px-3 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded-lg flex items-center justify-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" />
                    Replace
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  className="flex-1 py-1.5 px-3 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Caption */}
      <div className={`mt-2 ${SIZE_CLASSES[block.width]} ${ALIGNMENT_CLASSES[block.alignment]}`}>
        {isEditing ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a caption..."
              className="flex-1 px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-accent"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCaptionSave();
                if (e.key === 'Escape') setIsEditing(false);
              }}
            />
            <button
              onClick={handleCaptionSave}
              className="px-3 py-1.5 text-sm bg-accent text-white rounded-lg hover:bg-accent-dark"
            >
              Save
            </button>
          </div>
        ) : (
          <p
            className={`text-sm text-gray-500 italic text-${block.alignment} cursor-pointer hover:text-gray-700`}
            onClick={() => setIsEditing(true)}
          >
            {block.caption || 'Click to add caption...'}
          </p>
        )}
      </div>
    </div>
  );
}

export default VisualBlockComponent;
